import {Component,OnDestroy,OnInit} from '@angular/core';
import {PageHeaderItem} from "@share/component/page-header/page-header";
import {ActivatedRoute,Router} from "@angular/router";
import {IComponentWithRoutedTabs} from "@domain/admin/recherche/component-with-routed-tabs";
import {Store} from "@ngrx/store";
import {AppState} from "@domain/appstate";
import {Session} from "@domain/security/session";
import {filterFirstNotNull} from "@share/utils/rxjs-custom-operator";
import {isRouteAllowed} from "@core/security/role-admin-helpers";
import {Subscription} from "rxjs";
import {Location} from '@angular/common';

/**
 * Onglets
 */
export enum Onglets {
	ZONES_PREDEFINIES = 'ZonesPredefinies',
	ZONES_PARAMETRABLES = 'ZonesParametrables'
}

/**
 * Composant de la page Admin : Bibliothèque > Zones Utilisateurs
 */
@Component({
	host: {'data-test-id': 'zones-utilisateurs'},
	templateUrl: './zones-utilisateurs.component.html'
})
export class ZonesUtilisateursComponent implements OnInit, OnDestroy, IComponentWithRoutedTabs {
	/** Liste statique des onglets */
	public static listeOnglets: Array<PageHeaderItem> = [
		{
			code: Onglets.ZONES_PREDEFINIES,
			libelle: 'admin.bibliotheque.zonesUtilisateurs.tabs.predefinies',
			url: 'Admin/Bibliotheque/ZonesUtilisateurs/ZonesPredefinies'
		},{
			code: Onglets.ZONES_PARAMETRABLES,
			libelle: 'admin.bibliotheque.zonesUtilisateurs.tabs.parametrables',
			url: 'Admin/Bibliotheque/ZonesUtilisateurs/ZonesParametrables'
		}
	];

	/** Liste des onglets */
	listeTabItems: Array<PageHeaderItem> = ZonesUtilisateursComponent.listeOnglets;

	/** Index de l'onglet courant */
	selectedIndex: number;

	/** Indicateur de chargement de l'onglet ZONES_PREDEFINIES */
	isZonesPredefiniesLoaded: boolean = false;

	/** Indicateur de chargement de l'onglet ZONES_PARAMETRABLES */
	isZonesParametrablesLoaded: boolean = false;

	/** Onglet courant */
	_selectedItem: PageHeaderItem;

	/** Getter de l'onglet courant */
	get selectedItem(): PageHeaderItem {
		return this._selectedItem;
	}

	/** Setter de l'onglet courant (on le surcharge pour gérer le chargement des onglets) */
	set selectedItem(item: PageHeaderItem) {
		//Mise à jour de _selectedItem
		this._selectedItem = item;

		//Bascule du statut de chargement de l'onglet sélectionné
		switch (item.code) {
			case Onglets.ZONES_PREDEFINIES:
				this.isZonesPredefiniesLoaded = true;
				break;
			case Onglets.ZONES_PARAMETRABLES:
				this.isZonesParametrablesLoaded = true;
				break;
		}
	}

	/** Code de l'onglet à afficher au chargement de l'écran */
	onLoadTab: string;

	/** Import des onglets pour le DOM */
	readonly Onglets = Onglets;

	/** Session */
	private session: Session;

	/** Souscriptions */
	private subscriptions: Subscription[] = [];

	/**
	 * Constructeur
	 *
	 * @param router le routeur d'Angular
	 * @param store store
	 * @param activatedRoute route active
	 */
	constructor(
		private router: Router,
		private store: Store<AppState>,
		private location: Location,
		private activatedRoute: ActivatedRoute
	) {
		//Récupération du state de la route courante
		const state = this.router.getCurrentNavigation()?.extras.state || history.state;

		//Vérification de la présence d'un state sur la route
		if (state?.tabToLoad) {
			//Récupération de l'onglet à charger
			this.onLoadTab = state.tabToLoad;
		}
	}

	/**
	 * Initialisation du composant
	 */
	ngOnInit(): void {
		//On check si l'on doit afficher un onglet en particulier au chargement
		if (this.onLoadTab) {
			//Navigation vers cet onglet
			this.onSelectedItemChange(this.listeTabItems.find((value: PageHeaderItem) => value.code === this.onLoadTab));

			//Réinitialisation de onLoadTab
			delete this.onLoadTab;
		}

		//Récupération de la session
		this.store.select(state => state.session).pipe(filterFirstNotNull()).subscribe((session: Session) => {
			//Stockage de la session
			this.session = session;

			//Filtrage des onglets autorisés
			this.listeTabItems = this.listeTabItems.filter(i => isRouteAllowed(this.router, this.session, i.url));

			//Abonnement aux changements de route
			this.subscriptions.push(this.activatedRoute.url.subscribe(() => {
				//Lecture de l'onglet souhaité dans la route
				const onglet = this.activatedRoute.snapshot.firstChild.url[0].path;

				//On vérifie si on trouve l'onglet demandé via l'url
				const matching = Object.values(Onglets).find(v => v === onglet);
				if (!!matching) {
					//Si on trouve bien l'onglet correspondant, on met à jour le composant header
					this.selectedItem = this.listeTabItems.find(i => i.code === onglet);
					this.selectedIndex = this.listeTabItems.findIndex(i => i.code === onglet);
				} else {
					//Sinon, on redirige sur le premier onglet et on met à jour le composant header
					this.selectedItem = this.listeTabItems[0];
					this.selectedIndex = 0;
				}
			}));
		});
	}

	/**
	 * Changement d'onglet
	 *
	 * @param item l'onglet sélectionné
	 */
	onSelectedItemChange(item: PageHeaderItem) {
		//Mise à jour de l'indicateur d'activation pour chaque onglet
		this.listeTabItems?.forEach(tab => {
			tab.selected = tab === item;
		});

		//Mise à jour de l'onglet sélectionnée
		item.selected = true;
		this.selectedItem = item;
		this.selectedIndex = this.listeTabItems.findIndex(i => i.code === item.code);
		this.location.go(`/Admin/Bibliotheque/ZonesUtilisateurs/${item.code}`);
	}

	/**
	 * Destruction du composant
	 */
	ngOnDestroy() {
		//Résiliation des abonnements
		this.subscriptions.forEach(s => s.unsubscribe());
	}
}
